import { Component, ContentChild, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import {
  AlfrescoApiService,
  ContentService,
  CustomEmptyContentTemplateDirective,
  CustomLoadingContentTemplateDirective,
  CustomNoPermissionTemplateDirective,
  DataCellEvent,
  DataColumn,
  DataRowActionEvent,
  DataSorting,
  DataTableComponent,
  DisplayMode,
  LogService,
  PaginatedComponent,
  PaginationModel,
  RequestPaginationModel,
  ShowHeaderMode,
  ThumbnailService,
  TranslationService,
  UserPreferencesService,
  UserPreferenceValues
} from '@alfresco/adf-core';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  MinimalNodeEntity,
  NodeEntry,
  NodePaging,
  Pagination,
  RequestSortDefinition,
  ResultBuckets,
  ResultSetPaging,
  SearchApi,
  SearchRequest
} from '@alfresco/js-api';
import {
  ContentActionModel,
  DocumentListComponent,
  ShareDataRow,
  ShareDataTableAdapter
} from '@alfresco/adf-content-services';
import { Store } from '@ngrx/store';
import { filter, takeUntil } from 'rxjs/operators';
import { SetSelectedNodesAction, ViewNodeAction } from '@alfresco/aca-shared/store';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CustomListService } from '../../../services/custom-list.service';
import { CustomListQueryBuilderService } from '../../../services/custom-list-query-builder.service';
import { CustomDataColumn, CustomViewerConfig } from '../../../models/custom-viewer-config';
import { NavigationEnd, Router } from '@angular/router';
import { ShowHideFiltersHeaderTableAction } from '../../../store/actions/alden.actions';
import { hasAspect, hasPropertyValue } from '../../../rules/alden.rules';
import { ExtensionService, RuleService } from '@alfresco/adf-extensions';
import { AldenService } from '../../../services/alden.service';
import { CustomUserPreferencesService } from '../../../services/custom-user-preferences-service';

@Component({
  selector: 'smt-custom-list-table',
  templateUrl: './custom-list-table.component.html',
  styleUrls: ['./custom-list-table.component.scss'],
  host: { class: 'adf-document-list' }
})
export class CustomListTableComponent implements PaginatedComponent, OnInit, OnDestroy, OnChanges {

  private onDestroy$ = new Subject<boolean>();
  protected locale: string;

  _searchApi: SearchApi;
  get searchApi(): SearchApi {
    this._searchApi = this._searchApi ?? new SearchApi(this.alfrescoApiService.getInstance());
    return this._searchApi;
  }

  DEFAULT_PAGINATION: Pagination = new Pagination({
    hasMoreItems: false,
    skipCount: 0,
    maItems: 25,
    totalItems: 0
  });

  DEFAULT_SORTING: DataSorting[] = [new DataSorting('name', 'asc'), new DataSorting('isFolder', 'desc')];

  @ContentChild(CustomLoadingContentTemplateDirective)
  customLoadingContent: CustomLoadingContentTemplateDirective;

  @ContentChild(CustomNoPermissionTemplateDirective)
  customNoPermissionsTemplate: CustomNoPermissionTemplateDirective;

  @ContentChild(CustomEmptyContentTemplateDirective)
  customNoContentTemplate: CustomEmptyContentTemplateDirective;

  @Input()
  maxItems: number = this.DEFAULT_PAGINATION.maxItems;

  /** Change the display mode of the table. Can be "list" or "gallery". */
  @Input()
  display: string = DisplayMode.List;

  /** Toggles navigation to folder content or file preview */
  @Input()
  navigate = false;

  /** The ID of the folder node to display or a reserved string alias for special sources */
  @Input()
  currentFolderId: string = null;

  /** Show document thumbnails rather than icons */
  @Input()
  thumbnails = false;

  @Input()
  selectionMode; // none|single|multiple

  @Input()
  multiselect;

  @Input()
  sorting: DataSorting;

  @Input()
  additionalSorting: DataSorting;

  @Input()
  showHeader = ShowHeaderMode.Always;

  @Input()
  sortingMode: 'server' | 'client';

  /** Toggles the sticky header mode. */
  @Input()
  stickyHeader = false;

  @Input()
  imageResolver: any | null = null;

  @Input()
  columns: CustomDataColumn[];

  @Input()
  showMainDatatableActions: boolean;

  @Input()
  emptyFolderImageUrl = './assets/images/empty_doc_lib.svg';

  //Configuración del visor en el json
  @Input()
  viewerConfig: CustomViewerConfig;

  @Output()
  ready: EventEmitter<ResultSetPaging> = new EventEmitter<ResultSetPaging>();

  @Output()
  nodeSelected: EventEmitter<NodeEntry[]> = new EventEmitter<NodeEntry[]>();

  @Output()
  refreshToolbarActions: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  refreshFilters: EventEmitter<ResultBuckets[]> = new EventEmitter<ResultBuckets[]>();

  @ViewChild('dataTable', { static: true })
  dataTable: DataTableComponent;
  navigationMode: string = DocumentListComponent.DOUBLE_CLICK_NAVIGATION; //click//dblClick

  data: ShareDataTableAdapter;

  selection = new Array<NodeEntry>();
  orderBy: string[] = null;
  allowFiltering = true;

  nodes: NodePaging;

  isLoading = false;
  isSmallScreen = false;
  isFilterQuering = false;

  actions: ContentActionModel[] = [];
  contextActionHandler: Subject<any> = new Subject();

  private _pagination: PaginationModel = this.DEFAULT_PAGINATION;
  pagination: BehaviorSubject<PaginationModel> = new BehaviorSubject<PaginationModel>(this.DEFAULT_PAGINATION);
  sortingSubject: BehaviorSubject<DataSorting[]> = new BehaviorSubject<DataSorting[]>(this.DEFAULT_SORTING);

  //Objeto con elementos de las listas de datos
  dataListsElementsString: string;

  firstLoad: boolean;

  private initialUrl: string;
  private wasOutside = false;

  constructor(
    private userPreferencesServies: UserPreferencesService,
    private contentService: ContentService,
    private thumbnailService: ThumbnailService,
    private ngZone: NgZone,
    private alfrescoApiService: AlfrescoApiService,
    private logger: LogService,
    private store: Store<any>,
    private breakpointObserver: BreakpointObserver,
    private customListQueryBuilderService: CustomListQueryBuilderService,
    public customListService: CustomListService,
    private router: Router,
    protected extensions: ExtensionService,
    protected ruleService: RuleService,
    private translation: TranslationService,
    private aldenService: AldenService,
    private userPreferencesService: UserPreferencesService,
    private customUserPreferencesService: CustomUserPreferencesService
  ) {
    this.firstLoad = true;

    // Guardamos la URL que tiene el componente al crearse
    this.initialUrl = this.router.url;

    this.userPreferencesServies
       .select(UserPreferenceValues.PaginationSize)
       .pipe(takeUntil(this.onDestroy$))
       .subscribe((pagSize) => {
          this.maxItems = this._pagination.maxItems = pagSize;
    });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd), takeUntil(this.onDestroy$)).subscribe((event: NavigationEnd) => {
      const currentUrl = event.urlAfterRedirects;

      // Hemos salido de la URL inicial
      if (currentUrl !== this.initialUrl) {
        this.wasOutside = true;
        return;
      }

      // Hemos vuelto a la URL inicial
      if (this.wasOutside) {
        this.wasOutside = false;
        this.refreshToolbarActions.emit();
      }
    });
  }

  isMobile(): boolean {
    return !!/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  isEmpty() {
    return !this.data || this.data.getRows().length === 0;
  }

  async ngOnInit(): Promise<void> {
    this.customUserPreferencesService.defaultColumnsSet$.subscribe(cols => {
      if(cols){
        this.columns = cols.filter(column =>  !column.onlyFilter && (!column.visible || column.visible == "true" || (column.visible != "false" && this.extensions.evaluateRule(column.visible))));
        this.reloadList();
      }
    })
    //Cargar configuración en el service
    this.customListQueryBuilderService.loadConfiguration(this.viewerConfig);
    this.customUserPreferencesService.loadConfiguration(this.viewerConfig);
    
    const defaultConfigID = this.customUserPreferencesService.getDefaultConfig();
    if(defaultConfigID)
      await this.loadDefaultConfig(defaultConfigID);

    //Cargar columnas (solo aquellas que no sean solamente filtro y tenga permiso para ver)
    let columns = this.viewerConfig.columns.filter(column =>  !column.onlyFilter && (!column.visible || column.visible == "true" || (column.visible != "false" && this.extensions.evaluateRule(column.visible))));
    columns = columns.map((c, i) => ({
      ...c,
      originalIndex: i
    }));
    this.columns = columns;
    this.sortColumns();

    //Cargar elementos de datalist
    this.locale = this.userPreferencesService.locale;
    this.customListService.loadDataListsElements(this.columns);

    this.userPreferencesService.onChange.subscribe((userPreferenceStatus) => {
      const locale = userPreferenceStatus.locale;
      if(this.locale != locale) {
        this.locale = locale;
        this.customListService.loadDataListsElements(this.columns, true);
      }
    });

    //Suscribirse a objeto de DatalistElements
    this.customListService.dataListsElements.pipe(takeUntil(this.onDestroy$)).subscribe(
      obj => {
        this.dataListsElementsString = JSON.stringify(obj);
    });

    //Clear Filters
    this.customListQueryBuilderService.clearAndSetDefaultFilterQueries();

    //Mostrar filtros por defecto
    this.viewerConfig.showFiltersByDefault ? this.store.dispatch(new ShowHideFiltersHeaderTableAction()) : null;

    //Suscribirse al reloadTable
    let firstLoad = true;
    this.customListService.reloadTable.pipe(takeUntil(this.onDestroy$)).subscribe((res: { resetFilters: boolean }) => {
        if(res?.resetFilters === false) {
          this.reload(0, firstLoad);
          firstLoad = false;

          // El BehaviorSubject se acuerda del valor anterior. Se debe limpiar el subject para que el subscriptor no entre dos veces
          this.customListService.reloadTable.next(undefined);
        }
    });

    //Suscribirse al loadDataListElements
    this.customListService.loadElements.pipe(takeUntil(this.onDestroy$)).subscribe((res: { reset: boolean, resetOnlyDynamic?: boolean }) => {
      if(res) {
        this.customListService.loadDataListsElements(this.columns, res.reset, res.resetOnlyDynamic);
      }
    });

    this.data = new ShareDataTableAdapter(this.thumbnailService, this.contentService, null, this.sorting, this.sortingMode, false);

    this.data.thumbnails = this.thumbnails;

    this.data.setImageResolver(this.imageResolver);

    this.breakpointObserver
      .observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        this.isSmallScreen = result.matches;
      });

    this.enforceSingleClickNavigationForMobile();

    // Comentado porque ahora se encarga el custom-search-filter
    //this.reload();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(!this.firstLoad)
      this.resetSelection();
    this.firstLoad = false;

    this.orderBy = this.buildOrderByArray(this.sorting.key, this.sorting.direction);

    if (changes.sortingMode && !changes.sortingMode.firstChange && this.data) {
      this.data.sortingMode = changes.sortingMode.currentValue;
    }

    if (changes.sorting && !changes.sorting.firstChange && this.data) {
      const newValue = changes.sorting.currentValue;
      if (newValue && newValue.length > 0) {
        const [key, direction] = newValue;
        this.data.setSorting(new DataSorting(key, direction));
      }
    }

    if (this.data) {
      if (changes.imageResolver) {
        this.data.setImageResolver(changes.imageResolver.currentValue);
      }
    }
  }

  ngOnDestroy() {
    this.customListService.dataLength = 0;
    this.customListService.totalItems = 0;
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  reload(skipCount?: number, notReset?: boolean) {
    if(!notReset)
      this.resetSelection();
    this.ngZone.run(() => {
      if (this.nodes) {
        this.data.loadPage(this.nodes, false, null);
        this.onDataReady(this.nodes);
      } else {
        this.reloadList(skipCount);
      }
    });
  }

  private reloadList(skipCount?: number) {
    //console.debug("***********************RELOAD LIST***********************");
    this.customListQueryBuilderService.setPagination(this._pagination.maxItems, skipCount ?? this._pagination.skipCount);
    this.customListQueryBuilderService.buildQuery(this.getSortRequest());
    const query: SearchRequest = this.customListQueryBuilderService.getCurrentQuery();
    if(query){
      this.isLoading = true;
      this.ngZone.run(async () => {
        this.searchApi.search(query).then(
          (results: ResultSetPaging) => {
            this.data.loadPage(results as NodePaging, false, null);
            this.pagination.next(results.list?.pagination);
            this.onDataReady(results);

            this.customListService.dataLength = results.list?.entries?.length ?? 0;
            this.refreshToolbarActions.emit();
            this.refreshFilters.emit(results.list?.context?.facetsFields);

            this.isLoading = false;
          },
          (err) => {
            this.logger.debug(err);

            this.customListService.dataLength = 0;
            this.customListService.totalItems = 0;
            this.refreshToolbarActions.emit();
            this.refreshFilters.emit([]);
            this.isLoading = false;
          }
        );
      });
    }
  }

  private getSortRequest(): RequestSortDefinition {
    const sortRequest: RequestSortDefinition = this.orderBy?.map((order) => ({
      type: 'FIELD',
      field: order.split(' ')[0],
      ascending: order.split(' ')[1] === 'asc'
    }));
    return sortRequest;
  }

  private buildOrderByArray(currentKey: string, currentDirection: string): string[] {
    let orderByArray = [`${currentKey} ${currentDirection}`];

    if (this.additionalSorting) {
      orderByArray = [`${currentKey} ${currentDirection}`, `${this.additionalSorting.key} ${this.additionalSorting.direction}`];
    }

    return orderByArray;
  }

  resetSelection() {
    this.dataTable.resetSelection();
    this.dataTable.isSelectAllIndeterminate = false;
    this.selection = [];
    this.nodeSelected.emit(this.selection);
    this.store.dispatch(new SetSelectedNodesAction(this.selection));
  }

  private onDataReady(nodePaging: ResultSetPaging) {
    this.ready.emit(nodePaging);
    this.pagination.next(nodePaging.list.pagination);
    this.customListService.totalItems = nodePaging.list?.pagination?.totalItems ?? 0;
  }

  updatePagination(requestPaginationModel: RequestPaginationModel) {
    this._pagination.maxItems = requestPaginationModel.maxItems;
    this._pagination.merge = requestPaginationModel.merge;
    this._pagination.skipCount = requestPaginationModel.skipCount;
    this.reload();
  }

  trackById(_: number, obj: { id: string }) {
    return obj.id;
  }

  onNodeSelect(event: { row: ShareDataRow; selection: Array<ShareDataRow> }) {
    this.selection = event.selection.map((entry) => entry.node);
    this.nodeSelected.emit(this.selection);
    if (this.selection) {
      this.store.dispatch(new SetSelectedNodesAction(this.selection));
    }
  }

  onNodeUnselect(event: { row: ShareDataRow; selection: Array<ShareDataRow> }) {
    this.selection = event.selection.map((entry) => entry.node);
    this.nodeSelected.emit(this.selection);
    this.store.dispatch(new SetSelectedNodesAction(this.selection));
  }

  onRowDbClick(node: MinimalNodeEntity) {
    if(this.viewerConfig.dbClickPreview){
      this.store.dispatch(new ViewNodeAction(node.entry.id, { location: this.router.url }));
    } else {
      this.onRowDbClickAction(node);
    }
  }

  onRowDbClickAction(node: MinimalNodeEntity) {
    if (this.viewerConfig.dbClickAction) {
      const selection = this.aldenService.getSelectionState(node);
      this.store.dispatch({
        type: this.viewerConfig.dbClickAction,
        selection,
        payload: selection.nodes
      });
    }
  }

  onSortingChanged(event: CustomEvent) {
    this.orderBy = this.buildOrderByArray(
      event.detail.sortingKey !== undefined ? event.detail.sortingKey : event.detail.key.substring(event.detail.key.indexOf('.')+1, event.detail.key.length),
      event.detail.direction
    );
    if (this.additionalSorting) {
      this.sortingSubject.next([this.additionalSorting, event.detail]);
    } else {
      this.sortingSubject.next([event.detail]);
    }
    if (this.sortingMode === 'server') {
      this.reloadList();
    }
  }

  onColumnsVisibilityChange(updatedColumns: Array<DataColumn>): void {
    const visibilityMap = new Map(updatedColumns.map(c => [c.id, c.isHidden]));
    this.columns = this.columns.map(col => ({ ...col, isHidden: visibilityMap.get(col.id) ?? col.isHidden }));
    this.sortColumns();
    this.customListQueryBuilderService.columns = this.columns;
    this.data.setColumns(this.columns as any as DataColumn[]);
  }

  getClass(context: any): string {
    const classes = [];
    const highlights = this.viewerConfig.highlight;
    highlights?.forEach((highlight) =>  {
      if(highlight.aspect && hasAspect(context.row.obj, highlight.aspect)) {
        classes.push(highlight.class);
      }
      if(highlight.property && hasPropertyValue(context.row.obj, highlight.property.key, highlight.property.value)) {
        classes.push(highlight.class);
      }
    });
    return classes.join(' ');
  }

  async onShowRowActionsMenu(event: DataCellEvent) {
    //console.debug("onShowRowActionsMenu", event);
    event.value.actions = await this.aldenService.getContextActions(event.value as any);
    //console.debug(event.value.actions);
  }

  onExecuteRowAction(_event: DataRowActionEvent) {
    //console.debug("onExecuteRowAction", event);
    //console.debug(`${event.value.action.title} ${event.value.row.obj.firstName}`);
  }

  onColumnOrderChanged(columns: DataColumn[]) {
    const orderMap = new Map(columns.map((column, index) => [column.id, index]));

    this.customListQueryBuilderService.columns.sort((a, b) => {
      const posA = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const posB = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      return posA - posB;
    });
  }

  private sortColumns() {
    const sortAlphabetically = this.viewerConfig?.sortAlphabetically === true;

    const cols = [...this.columns];

    /**
     * Encadena comparadores: el primero que devuelva algo distinto de 0 decide.
     */
    const compare = (...fns: Array<(a: CustomDataColumn, b: CustomDataColumn) => number>) =>
      (a: any, b: any) => {
        for (const fn of fns) {
          const r = fn(a, b);
          if (r !== 0) return r;
        }
        return 0;
      };

    /**
     * 1. Visibilidad
     *    - Visibles primero
     *    - Ocultas después
     */
    const byVisibility = (a: CustomDataColumn, b: CustomDataColumn) => {
      const ah = !!a.isHidden;
      const bh = !!b.isHidden;
      return ah === bh ? 0 : ah ? 1 : -1;
    };

    /**
     * 2. Orden alfabético exclusivo para columnas ocultas
     *    - Si ambas están ocultas --> ignorar order y ordenar por título
     *    - Si no --> no decide nada
     */
    const byHiddenAlphabetical = (a: CustomDataColumn, b: CustomDataColumn) => {
      const ah = !!a.isHidden;
      const bh = !!b.isHidden;

      if (ah && bh) {
        const ta = this.translation.instant(a.title);
        const tb = this.translation.instant(b.title);
        return ta.localeCompare(tb);
      }

      return 0;
    };

    /**
     * 3. Orden por "order" (solo para visibles)
     *    - Si ambos tienen order --> comparar
     *    - Si solo uno tiene --> ese va primero
     *    - Si ninguno tiene --> no decide
     */
    const byOrder = (a: CustomDataColumn, b: CustomDataColumn) => {
      if (a.isHidden || b.isHidden) return 0; // ocultas no usan order

      const oa = a.order ?? null;
      const ob = b.order ?? null;

      const hasA = oa != null;
      const hasB = ob != null;

      if (hasA && hasB) return oa - ob;
      if (hasA) return -1;
      if (hasB) return 1;
      return 0;
    };

    /**
     * 4. Desempate cuando ambos visibles tienen el MISMO order
     */
    const byOrderTieBreak = (a: CustomDataColumn, b: CustomDataColumn) => {
      if (a.isHidden || b.isHidden) return 0;

      const oa = a.order ?? null;
      const ob = b.order ?? null;

      if (oa != null && ob != null && oa === ob) {
        if (sortAlphabetically) {
          const ta = this.translation.instant(a.title);
          const tb = this.translation.instant(b.title);
          return ta.localeCompare(tb);
        }
        return (a.originalIndex ?? 0) - (b.originalIndex ?? 0);
      }

      return 0;
    };

    /**
     * 5. Orden alfabético para visibles sin order
     */
    const byAlphabetical = (a: CustomDataColumn, b: CustomDataColumn) => {
      if (a.isHidden || b.isHidden) return 0;

      const oa = a.order ?? null;
      const ob = b.order ?? null;

      if (oa == null && ob == null && sortAlphabetically) {
        const ta = this.translation.instant(a.title);
        const tb = this.translation.instant(b.title);
        return ta.localeCompare(tb);
      }

      return 0;
    };

    /**
     * 6. Orden original como último recurso
     */
    const byOriginalOrder = (a: CustomDataColumn, b: CustomDataColumn) => (a.originalIndex ?? 0) - (b.originalIndex ?? 0);

    // Orden final aplicando comparadores en cascada
    cols.sort(
      compare(
        byVisibility,          // visibles primero
        byHiddenAlphabetical,  // ocultas ordenadas alfabéticamente
        byOrder,               // visibles con order
        byOrderTieBreak,       // desempate de order
        byAlphabetical,        // visibles sin order
        byOriginalOrder        // estabilidad
      )
    );

    this.columns = cols;
  }

  private enforceSingleClickNavigationForMobile(): void {
    if (this.isMobile()) {
      this.navigationMode = DocumentListComponent.SINGLE_CLICK_NAVIGATION;
    }
  }
    
  async loadDefaultConfig(id:string){
    const file = await this.aldenService.getContentOfFile(id);
    const jsonContent = JSON.parse(await file.text());
    this.viewerConfig.columns = jsonContent;
    this.customListQueryBuilderService.columns = jsonContent;
    this.customUserPreferencesService.columns = jsonContent;
  }
}
